import {
  Component,
  OnInit,
  ViewChild,
  Output,
  EventEmitter,
  computed,
  ElementRef,
  AfterViewChecked
} from '@angular/core';
import {MatIconModule} from '@angular/material/icon';
import {MatMenuModule, MatMenuTrigger} from '@angular/material/menu';
import {MatDividerModule} from '@angular/material/divider';
import {MatSlideToggleModule} from '@angular/material/slide-toggle';
import {MatButtonModule} from '@angular/material/button';
import {AsyncPipe, NgClass, NgIf} from '@angular/common';
import {TranslateModule} from '@ngx-translate/core';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatBadgeModule} from '@angular/material/badge';
import {combineLatest, iif, Observable, of, Subscription, timer} from 'rxjs';
import {filter, map, switchMap} from 'rxjs/operators';
import {Buffer} from 'buffer';
import {
  DialogWidth,
  Q9HtmlDecoderPipe,
  Q9TrackDirective,
  Q9TransformPipe,
  REPORT_MODAL_TYPE
} from '@q9elements/ui-kit/common';
import {
  IconButtonComponent,
  NotificationsModule,
  PrimaryButtonComponent,
  ReportsModule,
  RequestEditRightsDialogComponent,
  TagComponent,
  ThemeSwitcherComponent,
  ToolbarModule,
  TrainingsModule
} from '@q9elements/ui-kit/components';
import {
  MapShareDialogModule,
  MapShareDialogComponent,
  MapShareDialogData,
  Q9MapsDaoService
} from '@q9elements/ui-kit/maps';
import {DiagramDetailsComponent} from '../diagram-details/diagram-details.component';
import {UsersOnlineService} from '../users-online.service';
import {VersionsService} from '../../../core/services/versions.service';
import {DiagramJsonHandlerService} from '../../../diagram-data/diagram-json-handler.service';
import {AuthService} from '../../../core/auth/auth.service';
import {IToolbarMode, ToolbarStateService} from '../toolbar-state.service';
import {Title} from '@angular/platform-browser';
import {EncodeHtmlPipe} from '../../shared/pipes/encode-html.pipe';
import {environment} from '../../../../environments/environment';
import {DiagramMetaService} from '../../../diagram-data/diagram-meta.service';
import {GenerationProgressService} from '../../../shared/service/generation-progress.service';
import {ModeManagerService} from '../../../editor-state/mode-manager.service';
import {NavigationService} from '../../../core/navigation/navigation.service';
import {UndoService} from '../../../commands/undo.service';
import {DiagramTabsInEditEnum} from '../../shared/models/diagram-rsb-model';
import {SidebarService} from '../../sidebar/sidebar.service';
import {EditorRegistryService} from '../../../diagram-render/mx-graph/editor/editor-registry.service';
import {DiagramCommentsComponent} from '../diagram-comments/diagram-comments.component';
import {PopupManagerService} from '../../../core/services/popup-manager.service';
import {GraphExportService} from '../../../diagram-interaction/services/graph-export.service';
import {HelpDialogComponent} from '../help-dialog/help-dialog.component';
import {ClientEnvService} from '../../../core/services/client-env.service';
import {StickersService} from '../../../diagram-interaction/services/stickers.service';
import {SearchDiagramComponent} from '../search-diagram/search-diagram.component';
import {SfaHeaderService} from '../../../diagram-render/sfa-header/sfa-header.service';
import {MapCopyService} from '../../../core/map/map-copy.service';
import {DiagramNavigationComponent} from './diagram-navigation/diagram-navigation.component';
import {AuthorisationService} from '../../../core/services/authorisation.service';
import {TrainingNavigationComponent} from '../training-navigation/training-navigation.component';
import {AuthorisationNavigationComponent} from '../authorisation-navigation/authorisation-navigation.component';
import {TrainingsService} from '../../../core/services/trainings.service';
import {SyncUrlsPopupComponent} from '../sync-urls-popup/sync-urls-popup.component';
import {ChangelogsService} from 'src/app/core/services/changelogs.service';
import {diagramVersionsPublishing, shareDiagramLink} from 'src/app/shared/constants/elements-links';
import {CommonWrapperDialogService} from '../../../shared/service/common-wrapper-dialog.service';
import {DIAGRAM_VERSION_STATES_ENUM} from '../../shared/models/diagram-version.constant';
import {GetVersionTagColorPipe} from '../../shared/versions/get-version-tag-color.pipe';
import {DiagramTypeConfigService} from '../../../core/services/diagram-type-config.service';

declare let mxUtils: any;

@Component({
  selector: 'app-main-toolbar',
  templateUrl: './main-toolbar.component.html',
  styleUrls: ['./main-toolbar.component.scss'],
  standalone: true,
  imports: [
    ToolbarModule,
    NotificationsModule,
    ReportsModule,
    MatIconModule,
    MatMenuModule,
    MatDividerModule,
    MatSlideToggleModule,
    MatButtonModule,
    MatTooltipModule,
    MatBadgeModule,
    IconButtonComponent,
    NgIf,
    NgClass,
    TranslateModule,
    AsyncPipe,
    DiagramDetailsComponent,
    DiagramNavigationComponent,
    Q9TrackDirective,
    PrimaryButtonComponent,
    DiagramCommentsComponent,
    HelpDialogComponent,
    TrainingsModule,
    SearchDiagramComponent,
    TrainingNavigationComponent,
    AuthorisationNavigationComponent,
    MapShareDialogModule,
    ThemeSwitcherComponent,
    TagComponent,
    Q9HtmlDecoderPipe,
    GetVersionTagColorPipe,
    Q9TransformPipe
  ]
})
export class MainToolbarComponent implements OnInit, AfterViewChecked {
  public trainingMode$ = this.trainingSvc.trainingMode$;
  public authorisationMode$ = this.authorisationService.authorisationMode$;
  public toolbarState: IToolbarMode;
  public diagramJson: any;
  public diagramState: string;
  public user: any;
  public isPending: boolean;
  public diagramNameSubRow: string;
  public pendingText: string;
  public showSearch = false;
  public currentCategory = this.diagramTypeConfigSvc.currentType;
  public tagName = this.diagramTypeConfigSvc.tagName;
  public isAgent = this.diagramTypeConfigSvc.isAgent;
  public isArchitecture = this.diagramTypeConfigSvc.isArchitecture;
  public readonly diagramTagColor = computed(() => {
    return this.diagramTypeConfigSvc.generalSettings().tagColor;
  });
  public readonly isVersioningEnabled = computed(() => {
    return !this.diagramTypeConfigSvc.generalSettings().disableVersioning;
  });
  public usersOnline$: Observable<any>;
  public readonly DIAGRAM_VERSION_STATES_ENUM = DIAGRAM_VERSION_STATES_ENUM;
  public readonly diagramVersionsPublishing = diagramVersionsPublishing;
  private subscriptions: Subscription = new Subscription();
  private toolbarModeText$ = this.toolbarStateService.toolbarState$.pipe(
    map(({editMode}) => (editMode ? 'TOOLBAR.VIEW' : 'TOOLBAR.EDIT'))
  );
  public modeButtonTooltip$ = this.generationProgressService.dvGenerationInProgress$.pipe(
    switchMap(dvGenerationInProgress =>
      iif(
        () => dvGenerationInProgress,
        of('TOOLBAR.NO_EDIT_WHILE_GENERATING_TOOLTIP' as const),
        this.toolbarModeText$
      )
    )
  );
  private viewChecked = false;

  @ViewChild(MatMenuTrigger) trigger: MatMenuTrigger;
  @ViewChild('toolbar', {read: ElementRef}) toolbar: ElementRef;

  @Output() navigation = new EventEmitter<any>();

  public currVersion$ = this.versionsSvc.currVersion$.pipe(
    map(versionName => {
      return this.diagramJsonSvc.isPro() ? versionName : '';
    })
  );

  constructor(
    public diagramJsonSvc: DiagramJsonHandlerService,
    public generationProgressService: GenerationProgressService,
    public navSvc: NavigationService,
    public undoSvc: UndoService,
    public sidebarSvc: SidebarService,
    public stickersSvc: StickersService,
    public authSvc: AuthService,
    public mapCopyService: MapCopyService,
    public headerSvc: SfaHeaderService,
    private diagramMetaSvc: DiagramMetaService,
    private registry: EditorRegistryService,
    private usersOnlineSvc: UsersOnlineService,
    private modeManagerSvc: ModeManagerService,
    private versionsSvc: VersionsService,
    private toolbarStateService: ToolbarStateService,
    private titleSvc: Title,
    private encodeHtmlPipe: EncodeHtmlPipe,
    private popupSvc: PopupManagerService,
    private graphExportSvc: GraphExportService,
    private clientEnvSvc: ClientEnvService,
    private navigationSvc: NavigationService,
    private authorisationService: AuthorisationService,
    private trainingSvc: TrainingsService,
    private commonWrapperDialogService: CommonWrapperDialogService,
    public changelogSvc: ChangelogsService,
    private daoService: Q9MapsDaoService,
    private diagramTypeConfigSvc: DiagramTypeConfigService
  ) {}

  ngOnInit() {
    this.initializeObservablesAndSubscribe();
  }

  ngAfterViewChecked() {
    if (!this.viewChecked && this.toolbar) {
      mxUtils.writeElementBounds('MAIN_TOOLBAR_BOUNDS', this.toolbar);

      this.viewChecked = true;
    }
  }

  initializeObservablesAndSubscribe() {
    this.usersOnline$ = this.usersOnlineSvc.usersOnlineJson$;
    this.subscriptions.add(this.authSvc.user$.subscribe(user => (this.user = user)));
    this.subscriptions.add(
      this.toolbarStateService.toolbarState$.subscribe(stateObj => {
        this.toolbarState = stateObj;
      })
    );
    this.subscriptions.add(
      this.diagramJsonSvc.diagramJson$
        .pipe(filter(nextValue => nextValue.team))
        .subscribe(nextValue => {
          this.diagramJson = nextValue;
          this.diagramState = nextValue.state;
          this.versionsSvc.setStartReleaseVersionName(nextValue);
          this.isPending = nextValue.levelNumber === 'pending';

          if (this.isPending) {
            this.pendingText = 'pending';
            this.diagramNameSubRow = '';
            this.titleSvc.setTitle(
              `Level ${this.pendingText} Level ${this.encodeHtmlPipe.transform(
                nextValue.map.name,
                true
              )}`
            );
          } else {
            this.pendingText = '';
            const currentMapName = nextValue.topLevel ? nextValue.name : nextValue.map.name;

            this.diagramNameSubRow = `Level ${
              nextValue.levelNumber
            } of ${this.encodeHtmlPipe.transform(currentMapName, true)}`;
            this.titleSvc.setTitle(this.diagramNameSubRow);
          }
        })
    );
  }

  public toggleEditMode() {
    if (this.navSvc.isNavActive()) {
      return;
    }

    this.modeManagerSvc.toggle();
  }

  public navigateToDraftVersion(diagramId: string) {
    this.navigationSvc.navigate(diagramId, 'draft', false);
  }

  switchToVersion() {
    const graph = this.registry.getGraph();

    graph.stopEditing();
    graph.selectionModel.clear();
    this.sidebarSvc.diagramTabIndex.next(DiagramTabsInEditEnum.VERSIONS);

    if (!this.sidebarSvc.rightSidebar.opened) {
      timer(100).subscribe(() => this.sidebarSvc.toggle('right'));
    }
  }

  openReportsDialog(modalType: REPORT_MODAL_TYPE) {
    this.commonWrapperDialogService.openReportsDialog(
      modalType,
      this.diagramJsonSvc.get().team.id,
      this.navSvc.getCurrent().state,
      this.diagramJsonSvc.get().diagramId,
      this.diagramJsonSvc.get().map.type,
      this.diagramTypeConfigSvc.generalSettings().enabledReportTypes
    );
  }

  openRequestRightsDialog() {
    const mapId = this.diagramJson.map.id;

    const onSubmit = (message: string) => this.daoService.requestEditRights(mapId, message);

    return this.popupSvc.open(RequestEditRightsDialogComponent, {
      width: DialogWidth.SM,
      data: {
        modalTitle: 'CORE.MAP.REQUEST_RIGHTS_TITLE',
        label: 'CORE.MAP.REQUEST_RIGHTS_DESCRIPTION',
        onSubmit
      }
    });
  }

  public openShareDiagramPopup() {
    this.popupSvc.open<MapShareDialogComponent, MapShareDialogData>(MapShareDialogComponent, {
      width: DialogWidth.LG,
      panelClass: ['q9-dialog'],
      data: {
        modalTitle: 'SHARE_DIAGRAM.INVITE_MODAL_TITLE',
        confirmText: 'SHARE_DIAGRAM.INVITE_MODAL_BTN_INVITE',
        link: shareDiagramLink,
        macr: this.authSvc.getMACR(),
        sharedMap: {
          ...this.diagramJson.map,
          author: {
            ...this.diagramJson.map.author,
            id: this.diagramJson.author.id
          },
          subtype: this.diagramJson.subtype,
          topLevelDiagram: {id: this.diagramJson.diagramId},
          topLevelDiagramState: this.diagramJson.state
        },
        diagramId: this.diagramJson.id
      }
    });
  }

  public navigateToMapList() {
    const map = this.diagramJsonSvc.get().map;
    const folderIdRaw = map?.folderId;
    const folderId = this.normalizeFolderId(folderIdRaw);
    const index = this.authSvc.getTeamIndex() || 0;
    const mapList = folderId ? `/maps?ref=active&folderId=${folderId}` : '/maps';

    window.open(environment.MAIN_APP_URL + '/t/' + index + mapList, this.clientEnvSvc.anchorTarget);
  }

  public navigateToMapMgt() {
    const {
      topLevel,
      diagramId,
      map: {id: mapId}
    } = this.diagramJsonSvc.get();
    const url = [
      environment.MAP_MANAGEMENT_APP,
      '/manage/',
      mapId,
      '/tree',
      !topLevel ? '?root=' + diagramId : ''
    ].join('');

    window.open(url, this.clientEnvSvc.anchorTarget);
  }

  public openSearch(): void {
    this.showSearch = !this.showSearch;
  }

  public toggleMapTree() {
    this.popupSvc.toggleMapTree();
  }

  public print() {
    this.graphExportSvc.printPrepare();
  }

  public openSyncUrlPopup() {
    const dialogRef = this.popupSvc.open(SyncUrlsPopupComponent, {
      panelClass: ['q9-dialog'],
      width: DialogWidth.MD
    });

    dialogRef.afterClosed().subscribe(success => {
      if (success) {
        this.navSvc.current(true);
      }
    });
  }

  public copyMap(): void {
    this.mapCopyService.copyMap();
  }

  public copyDisabledReasonTooltip$ = combineLatest([
    this.mapCopyService.disabledCopy$,
    this.generationProgressService.dvGenerationInProgress$
  ]).pipe(
    map(([disableCopyReason, dvGenerationInProgress]) => {
      if (!disableCopyReason && !dvGenerationInProgress) {
        return '';
      }

      if (dvGenerationInProgress) {
        return 'TOOLBAR.NO_EDIT_WHILE_GENERATING_TOOLTIP';
      }

      if (disableCopyReason.isProTrial) {
        return 'MAP_COPY.NO_COPY_IN_PRO_TRIAL_TOOLTIP';
      }

      if (disableCopyReason.permissions) {
        return 'MAP_COPY.DISABLED_COPY_HINT';
      }
    })
  );

  /**
   * Converts a folderId that may be a string, Buffer object or other type to a valid string.
   * Handles Firebase-stored MongoDB ObjectIds that come as {buffer: Array(12)} objects.
   */
  private normalizeFolderId(
    folderId: string | null | {buffer: Array<number>} | undefined
  ): string | null {
    if (!folderId) {
      return null;
    }

    if (typeof folderId === 'string') {
      return folderId;
    }

    if (folderId?.buffer && Array.isArray(folderId.buffer) && folderId.buffer.length === 12) {
      return Buffer.from(folderId.buffer).toString('hex');
    }

    return String(folderId);
  }
}
